/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import { store } from 'redux/store';
import { useHistory } from 'react-router-dom';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import actionTypes from 'constants/actionTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import {
  IncentiveOffer,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { getLinkedTo } from 'modules/documents/components/Modals/UploadDocumentModal/utils/getLinkedTo';
import {
  addDocument,
  updateDocument,
} from 'modules/documents/redux/documentActions';
import { subcategoryTypes } from 'modules/incentivesMarket/constants/incentiveOfferOperationDocumentTypes';
import {
  addIncentiveOffer,
  updateIncentiveOffer,
} from 'modules/incentivesMarket/redux/incentiveOfferActions';
import { collectPublisherAuditContext } from 'modules/incentivesMarket/utils/collectPublisherAuditContext';
import { updateBeneficiary } from 'modules/incentivesMarket/api/beneficiaryActions';

import {
  PublishIncentiveOfferDraft,
  PublishIncentiveOfferModalContextType,
  PublisherPartner,
} from '../types/PublishIncentiveOfferModalContextType';
import { publishIncentiveOfferModalInitValue } from '../constants/publishIncentiveOfferModalInitValue';
import { useGetValidityDays } from '../hooks/useGetValidityDates';
import { buildInitialPublisherBeneficiary } from '../utils/buildInitialPublisherBeneficiary';

export const PublishIncentiveOfferModalContext =
  createContext<PublishIncentiveOfferModalContextType>(
    publishIncentiveOfferModalInitValue
  );

export const PublishIncentiveOfferModalProvider: FC<{
  children: ReactNode;
  incentiveOffer: PublishIncentiveOfferDraft;
  modalActionType?: string;
  size?: string;
  publisherPartner?: Partial<IncentivesMarketBeneficiary> | null;
}> = ({
  children,
  incentiveOffer,
  modalActionType = actionTypes.ADD,
  publisherPartner: publisherPartnerProp = null,
}) => {
  const { t } = useTranslate();
  const history = useHistory();

  const user = useSelector((state: { user?: { _id?: string } }) => state.user);

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] =
    useState<boolean>(false);
  const [isPurchaseContractAccepted, setIsPurchaseContractAccepted] =
    useState<boolean>(false);
  const [isHideSellerName, setIsHideSellerName] = useState<boolean>(
    Boolean(incentiveOffer.isSellerNameHidden)
  );
  const [emailSubsidiary, setEmailSubsidiary] = useState<string>(
    incentiveOffer.emailSubsidiary || ''
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [publishedOffer, setPublishedOffer] = useState<IncentiveOffer | null>(
    null
  );
  const [publisherDocuments, setPublisherDocuments] = useState<File[]>([]);
  const [editablePublisherPartner, setEditablePublisherPartner] =
    useState<PublisherPartner | null>(() =>
      buildInitialPublisherBeneficiary(publisherPartnerProp, incentiveOffer)
    );

  const { data: validityDaysData } = useGetValidityDays({
    startDate: incentiveOffer.startDate,
    endDate: incentiveOffer.endDate,
  });

  const handleCloseModal = () => {
    store.dispatch(setModal(null));
  };

  const handleNextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, 2));
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleChangeTermsAndConditionsAcceptance = () => {
    setIsTermsAndConditionsAccepted((prev) => !prev);
  };

  const handleChangePurchaseContractAcceptance = () => {
    setIsPurchaseContractAccepted((prev) => !prev);
  };

  const handleChangeHideSellerName = () => {
    setIsHideSellerName((prev) => !prev);
  };

  const handleAddPublisherDocument = (file: File) => {
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
    ];
    if (file.size > maxSize) {
      errorSwal(t('FileTooLargeMax5MB'));
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      errorSwal(t('InvalidFileTypePDFJPGPNG'));
      return;
    }
    setPublisherDocuments([file]);
  };

  const handleRemovePublisherDocument = (index: number) => {
    setPublisherDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePublisherPartnerField = (field: string, value: string) => {
    setEditablePublisherPartner((prev) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  const handleUpdateEmailSubsidiary = (value: string) => {
    setEmailSubsidiary(value);
  };

  const handleUpdatePublisherPartnerAddressField = (
    field: string,
    value: string
  ) => {
    setEditablePublisherPartner((prev) => {
      if (!prev) return null;
      const currentAddress = prev.address || {
        line1: '',
        zip: '',
        city: '',
        state: '',
        country: '',
      };
      return {
        ...prev,
        address: { ...currentAddress, [field]: value },
      };
    });
  };

  const resolveBeneficiaryId = (): string | undefined => {
    if (editablePublisherPartner?._id) {
      return editablePublisherPartner._id;
    }
    const { beneficiary } = incentiveOffer;
    if (!beneficiary) return undefined;
    if (typeof beneficiary === 'string') return beneficiary;
    return beneficiary._id;
  };

  const resolveSocietyId = (): string => {
    const { society } = incentiveOffer;
    return typeof society === 'string' ? society : '';
  };

  const handlePublishIncentiveOffer = async () => {
    const beneficiaryId = resolveBeneficiaryId();
    const societyId = resolveSocietyId();

    if (!beneficiaryId || !editablePublisherPartner) {
      errorSwal(t('ErrorPublishingIncentiveOffer'));
      return;
    }

    try {
      setIsLoading(true);

      const publisherDocumentIds: string[] = [];

      for (const file of publisherDocuments) {
        const fileType =
          fileTypes[file.type as keyof typeof fileTypes] || 'PDF';
        const newDocument = {
          file,
          date: new Date(),
          size: file.size,
          name: file.name,
          author: user?._id,
          society: societyId,
          fileType,
          category: documentTypes.DOCUMENTS,
          subcategory:
            subcategoryTypes.TRADING_OFFER_OPERATION.IDENTITY_DOCUMENT,
          description: t('IdentityDocumentForIncentiveOffer'),
        };
        const uploadedDoc = await store.dispatch(addDocument(newDocument));
        if (uploadedDoc?._id) {
          publisherDocumentIds.push(uploadedDoc._id);
        }
      }

      const priorDocuments = Array.isArray(incentiveOffer.documents)
        ? incentiveOffer.documents
            .map((d) => (typeof d === 'string' ? d : d?._id))
            .filter(
              (id): id is string => typeof id === 'string' && id.length > 0
            )
        : [];

      await store.dispatch(
        updateBeneficiary({
          beneficiaryId,
          updateBeneficiaryData: {
            name: editablePublisherPartner.name,
            cif: editablePublisherPartner.cif,
            email: editablePublisherPartner.email,
            phone: editablePublisherPartner.phone,
            bankAccountNumber: editablePublisherPartner.bankAccountNumber,
            address: editablePublisherPartner.address,
            userId: user?._id || '',
            society: societyId,
          },
        })
      );

      const documentsMerged = [...priorDocuments, ...publisherDocumentIds];

      let createdOffer: IncentiveOffer | undefined;
      let offerIdForLink: string | undefined;

      if (modalActionType === actionTypes.EDIT && incentiveOffer._id) {
        await store.dispatch(
          updateIncentiveOffer(incentiveOffer._id, {
            unitsCount: { total: incentiveOffer.unitsCount?.total ?? 0 },
            unitPrice: Number(incentiveOffer.unitPrice ?? 0),
            startDate: incentiveOffer.startDate as string,
            endDate: incentiveOffer.endDate as string,
            isPartialAmountEnabled: Boolean(incentiveOffer.isPartialAmountEnabled),
            minUnitsPerAcceptance: incentiveOffer.minUnitsPerAcceptance,
            isSellerNameHidden: isHideSellerName,
            documents: documentsMerged,
            emailSubsidiary,
          })
        );
        offerIdForLink = incentiveOffer._id;
        successSwal(t('OfferUpdatedSuccessfully'));
      } else {
        const dispatchResult = (await store.dispatch(
          addIncentiveOffer({
            ...incentiveOffer,
            emailSubsidiary,
            documents: documentsMerged,
            isSellerNameHidden: isHideSellerName,
            publisherAuditContext: collectPublisherAuditContext(),
          })
        )) as IncentiveOffer | undefined;

        const state = store.getState() as { incentiveOffers?: IncentiveOffer[] };
        createdOffer = dispatchResult ?? state.incentiveOffers?.[0];
        offerIdForLink = createdOffer?._id;
      }

      if (offerIdForLink && publisherDocumentIds.length > 0) {
        await Promise.all(
          publisherDocumentIds.map((docId) =>
            store.dispatch(
              updateDocument(
                {
                  _id: docId,
                  linkedTo: getLinkedTo({ tradingOfferId: offerIdForLink }),
                },
                false
              )
            )
          )
        );
      }

      setIsLoading(false);

      if (modalActionType === actionTypes.EDIT) {
        handleCloseModal();
        const sid =
          typeof incentiveOffer.society === 'string' ? incentiveOffer.society : '';
        history.push(`/ofertas-incentivos/${sid}${history.location.search || ''}`);
        return;
      }

      if (createdOffer) {
        setPublishedOffer(createdOffer);
      } else {
        handleCloseModal();
        history.push(`/ofertas-incentivos/${incentiveOffer.society}`);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingOffer')
          : t('ErrorPublishingIncentiveOffer');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <PublishIncentiveOfferModalContext.Provider
      value={{
        data: {
          modalActionType,
          incentiveOffer,
          publishedOffer,
          validityDays: validityDaysData.validityDays,
          isTermsAndConditionsAccepted,
          isPurchaseContractAccepted,
          isHideSellerName,
          isLoading,
          currentStep,
          publisherPartner: editablePublisherPartner,
          publisherDocuments,
          emailSubsidiary,
        },
        actions: {
          handleCloseModal,
          handleNextStep,
          handlePrevStep,
          handleChangeTermsAndConditionsAcceptance,
          handleChangePurchaseContractAcceptance,
          handleChangeHideSellerName,
          handleAddPublisherDocument,
          handleRemovePublisherDocument,
          handleUpdatePublisherPartnerField,
          handleUpdatePublisherPartnerAddressField,
          handleUpdateEmailSubsidiary,
          handlePublishIncentiveOffer,
        },
      }}
    >
      {children}
    </PublishIncentiveOfferModalContext.Provider>
  );
};

export const usePublishIncentiveOfferModal = () => {
  const context = useContext(PublishIncentiveOfferModalContext);
  if (!context) {
    throw new Error(
      'usePublishIncentiveOfferModal must be used within a PublishIncentiveOfferModalProvider'
    );
  }
  return context;
};
